import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { List, X } from "@phosphor-icons/react";
import { scrollToId } from "@/lib/lenis";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Workshops", href: "#workshops" },
  { label: "Consulting", href: "#consulting" },
  { label: "Why Us", href: "#why-us" },
  { label: "Contact", href: "#contact" },
];

const Wordmark = () => (
  <button
    data-testid="nav-logo"
    onClick={() => scrollToId("#top")}
    aria-label="Network in Colors, back to top"
  >
    <img
      src="/assets/logo.webp"
      alt="Network in Colors"
      className="h-10 md:h-12 w-auto"
    />
  </button>
);

const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToId(href), open ? 250 : 0);
  };

  return (
    <>
      <motion.header
        initial={{ y: -80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
        className={`fixed top-0 inset-x-0 z-50 transition-[background-color,border-color,box-shadow] duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-white/80 border-b border-black/5 shadow-[0_8px_30px_rgb(0,0,0,0.04)]"
            : "bg-transparent"
        }`}
      >
        <nav className="max-w-7xl mx-auto px-6 md:px-10 h-[72px] flex items-center justify-between">
          <Wordmark />
          <div className="hidden md:flex items-center gap-9">
            {LINKS.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                onClick={() => go(l.href)}
                className="text-sm font-semibold text-ink-soft hover:text-terracotta transition-colors duration-300"
              >
                {l.label}
              </button>
            ))}
            <button
              data-testid="nav-cta-button"
              onClick={() => go("#contact")}
              className="rounded-full bg-ink text-white text-sm font-semibold px-6 py-2.5 hover:bg-terracotta hover:scale-105 transition-[background-color,transform] duration-300"
            >
              Schedule a Consultation
            </button>
          </div>
          <button
            data-testid="nav-mobile-menu-button"
            className="md:hidden text-ink"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
          >
            <List size={28} weight="bold" />
          </button>
        </nav>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-[60] bg-ink text-white flex flex-col"
          >
            <div className="flex items-center justify-between px-6 h-[72px]">
              <span className="font-display text-xl font-bold">
                Network <span className="font-serif italic font-medium text-terracotta-light">in Colors</span>
              </span>
              <button
                data-testid="nav-mobile-close-button"
                onClick={() => setOpen(false)}
                aria-label="Close menu"
              >
                <X size={30} weight="bold" />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-2">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/\s/g, "-")}`}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.08, duration: 0.5 }}
                  onClick={() => go(l.href)}
                  className="text-left font-display text-4xl font-bold py-3 border-b border-white/10 hover:text-terracotta-light transition-colors"
                >
                  {l.label}
                </motion.button>
              ))}
              <motion.button
                data-testid="nav-mobile-cta-button"
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5, duration: 0.5 }}
                onClick={() => go("#contact")}
                className="mt-8 rounded-full bg-terracotta text-white font-semibold px-8 py-4 text-lg"
              >
                Schedule a Consultation
              </motion.button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navigation;
